/**
 * useHomeENDemoInvoicePageListLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait HomeENDemoInvoicePageList
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, HomeENDemoInvoicePageItem, HomeENDemoInvoicePageListEventPayloadMap } from '@app/shared';
import { useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import type { BusEvent } from '@almadar/core';
import { useTranslate } from '@almadar/ui/hooks';

// ============================================================================
// State Machine
// ============================================================================

export type HomeENDemoInvoicePageListState =
  | 'loading'
  | 'browsing'
  | 'error';

export type HomeENDemoInvoicePageListEvent =
  | 'INIT'
  | 'BrowseItemLoaded'
  | 'BrowseItemLoadFailed'
  | 'REFETCH_QUERY'
  | 'REFETCH_FILTER'
  | 'REFETCH_PAGE'
  | 'VIEW'
  | 'CLOSE'
  | 'EDIT'
  | 'DELETE'
  | 'INVOICE_ONE_CHANGED'
  | 'INVOICE_FIVE_CHANGED';

type HomeENDemoInvoicePageListSlot = 'main' | 'toast';
interface HomeENDemoInvoicePageListInternalState {
  machineState: HomeENDemoInvoicePageListState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeENDemoInvoicePageListEvent | null;
  slotWriter: Partial<Record<HomeENDemoInvoicePageListSlot, HomeENDemoInvoicePageListEvent | null>>;
  loading: boolean;
  pendingEvent: HomeENDemoInvoicePageListEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeENDemoInvoicePageItem>;
}

type HomeENDemoInvoicePageListAction =
  | { type: 'EVENT_SUCCESS'; event: HomeENDemoInvoicePageListEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: HomeENDemoInvoicePageListSlot[]; renderClear?: HomeENDemoInvoicePageListSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: HomeENDemoInvoicePageListEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof HomeENDemoInvoicePageItem & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function homeENDemoInvoicePageListReducer(
  state: HomeENDemoInvoicePageListInternalState,
  action: HomeENDemoInvoicePageListAction
): HomeENDemoInvoicePageListInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as HomeENDemoInvoicePageListState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<HomeENDemoInvoicePageItem>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<HomeENDemoInvoicePageItem>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<HomeENDemoInvoicePageItem>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: HomeENDemoInvoicePageListInternalState = {
  machineState: 'loading',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

import type { SExpr } from '@almadar/core';

export interface HomeENDemoInvoicePageListConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  bodyContent: { children: Array<Array<Array<number | string | string[]> | string | { children: FieldValue[]; gap: string; type: string } | { children: { entity: string; event: string; filters: string; look: string; type: string }[]; direction: string; gap: string; type: string }> | Array<string | { children: FieldValue[]; gap: string; type: string } | { children: { className: string; clearable: boolean; event: string; placeholder: string; type: string }[]; direction: string; gap: string; type: string }> | { cols: string; emptyDescription: string; emptyIcon: string; emptyTitle: string; entity: string; fields: string; gap: string; imageField: string; itemActions: Array<Array<Array<Array<Array<Array<FieldValue[] | string> | string> | number | string> | Array<Array<FieldValue[] | string> | string> | string> | string> | string>; itemClickEvent: string; itemEnter: string; maxInlineActions: string; pageSize: string; surface: string; type: string }>; direction: string; gap: string; type: string };
  bodySearch: boolean;
  browseLook: 'table' | 'dense' | 'feed' | 'gallery' | 'master-detail' | 'triage';
  cols: number;
  columns: Array<{ align: string; field: string; format: string; header: string[]; key: string } | { colorMap: { draft: string; overdue: string; paid: string; sent: string }; field: string; format: string; header: string[]; key: string; labels: { draft: string[]; overdue: string[]; paid: string[]; sent: string[] } } | { field: string; header: string[]; key: string } | { field: string; header: string[]; key: string; weight: string }>;
  denseBodyContent: { children: Array<Array<Array<number | string | string[]> | string | { children: FieldValue[]; gap: string; type: string } | { children: { entity: string; event: string; filters: string; look: string; type: string }[]; className: string; direction: string; gap: string; type: string }> | Array<string | { children: FieldValue[]; gap: string; type: string } | { children: { className: string; clearable: boolean; event: string; placeholder: string; type: string }[]; className: string; direction: string; gap: string; type: string }> | { children: { columns: string; emptyDescription: string; emptyIcon: string; emptyTitle: string; entity: string; itemActions: Array<Array<Array<Array<Array<Array<FieldValue[] | string> | string> | number | string> | Array<Array<FieldValue[] | string> | string> | string> | string> | string>; itemClickEvent: string; look: string; maxInlineActions: string; pageSize: string; selectable: string; surface: string; type: string }[]; className: string; direction: string; gap: string; type: string }>; className: Array<string | string[]>; direction: string; gap: string; type: string };
  detailActions: { event: string; label: string }[];
  detailCloseEvent: string;
  detailFields: Array<{ format: string; label: string; name: string } | { label: string; name: string; variant: string }>;
  displayPageSize: number;
  emptyDescription: string;
  emptyIcon: string;
  emptyTitle: string;
  feedBodyContent: { children: Array<Array<Array<number | string | string[]> | string | { children: FieldValue[]; gap: string; type: string } | { children: { entity: string; event: string; filters: string; look: string; type: string }[]; className: string; direction: string; gap: string; type: string }> | Array<string | { align: string; children: { className: string; clearable: boolean; event: string; placeholder: string; type: string }[]; className: string; direction: string; gap: string; type: string } | { children: FieldValue[]; gap: string; type: string }> | { emptyDescription: string; emptyIcon: string; emptyTitle: string; entity: string; fields: string; gap: string; itemActions: Array<Array<Array<Array<Array<Array<FieldValue[] | string> | string> | number | string> | Array<Array<FieldValue[] | string> | string> | string> | string> | string>; itemClickEvent: string; itemEnter: string; look: Array<string | string[]>; maxInlineActions: string; pageSize: string; surface: Array<string | string[]>; type: string; variant: Array<string | string[]> }>; className: string; direction: string; gap: string; type: string };
  feedSurface: 'cards' | 'divided' | 'plain';
  fields: Array<{ colorMap: { draft: string; overdue: string; paid: string; sent: string }; labels: { draft: string[]; overdue: string[]; paid: string[]; sent: string[] }; name: string; variant: string } | { format: string; name: string; variant: string } | { name: string; variant: string }>;
  filterBarLook: 'toolbar' | 'chips' | 'pills' | 'popover-trigger' | 'inline-column-header';
  filters: { field: string; label?: string; options?: string[] }[];
  galleryBodyContent: { children: Array<Array<Array<number | string | string[]> | string | { children: FieldValue[]; gap: string; type: string } | { children: { entity: string; event: string; filters: string; look: string; type: string }[]; className: string; direction: string; gap: string; type: string }> | Array<string | { align: string; children: { className: string; clearable: boolean; event: string; placeholder: string; type: string }[]; className: string; direction: string; gap: string; type: string } | { children: FieldValue[]; gap: string; type: string }> | { className: string; emptyDescription: string; emptyIcon: string; emptyTitle: string; entity: string; fields: string; gap: string; imageField: string; itemActions: Array<Array<Array<Array<Array<Array<FieldValue[] | string> | string> | number | string> | Array<Array<FieldValue[] | string> | string> | string> | string> | string>; itemClickEvent: string; itemEnter: string; maxInlineActions: string; minCardWidth: number; surface: string; type: string }>; className: string; direction: string; gap: string; type: string };
  gap: string;
  imageField: string;
  include: string[];
  initialFilterField: string;
  initialFilterValue: string;
  itemActions: { event?: string; icon?: string; label: string; navigatesTo?: string; roles?: string[]; variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; when?: SExpr }[];
  itemClickEvent: string;
  itemEnter: 'none' | 'fade' | 'rise' | 'scale' | 'slide';
  masterDetailBodyContent: { children: Array<Array<Array<number | string | string[]> | string | { children: FieldValue[]; gap: string; type: string } | { children: { entity: string; event: string; filters: string; look: string; type: string }[]; className: string; direction: string; gap: string; type: string }> | Array<string | { children: FieldValue[]; gap: string; type: string } | { children: { className: string; clearable: boolean; event: string; placeholder: string; type: string }[]; className: string; direction: string; gap: string; type: string }> | { detail: { children: FieldValue[]; gap: string; type: string }; emptyDetail: { description: string; icon: string; title: string; type: string }; hasSelection: boolean; master: { children: string[]; className: string; direction: string; gap: string; type: string }; type: string }>; className: Array<string | string[]>; direction: string; gap: string; type: string };
  masterDetailRecordBodyContent: { children: Array<Array<Array<number | string | string[]> | string | { children: FieldValue[]; gap: string; type: string } | { children: { entity: string; event: string; filters: string; look: string; type: string }[]; className: string; direction: string; gap: string; type: string }> | Array<string | { children: FieldValue[]; gap: string; type: string } | { children: { className: string; clearable: boolean; event: string; placeholder: string; type: string }[]; className: string; direction: string; gap: string; type: string }> | { detail: { actions: Array<Array<Array<Array<Array<Array<FieldValue[] | string> | string> | number | string> | Array<Array<FieldValue[] | string> | string> | string> | string> | string>; closeEvent: string; fields: string; initialData: string; showActions: boolean; surface: string; type: string }; hasSelection: boolean; master: { children: string[]; className: string; direction: string; gap: string; type: string }; type: string }>; className: Array<string | string[]>; direction: string; gap: string; type: string };
  maxInlineActions: number;
  orderBy: string;
  pageSize: number;
  scopeField: string;
  searchField: string;
  searchPlaceholder: string;
  selectable: boolean;
  surface: 'auto' | 'none';
  triageBodyContent: { children: Array<Array<Array<number | string | string[]> | string | { children: FieldValue[]; gap: string; type: string } | { children: { entity: string; event: string; filters: string; look: string; type: string }[]; className: string; direction: string; gap: string; type: string }> | Array<string | { children: FieldValue[]; gap: string; type: string } | { children: { className: string; clearable: boolean; event: string; placeholder: string; type: string }[]; className: string; direction: string; gap: string; type: string }> | { children: { emptyDescription: string; emptyIcon: string; emptyTitle: string; entity: string; fields: string; itemActions: Array<Array<Array<Array<Array<Array<FieldValue[] | string> | string> | number | string> | Array<Array<FieldValue[] | string> | string> | string> | string> | string>; itemClickEvent: string; itemEnter: string; maxInlineActions: string; pageSize: string; surface: string; type: string; variant: string }[]; className: string; direction: string; gap: string; type: string }>; className: Array<string | string[]>; direction: string; gap: string; type: string };
  viewerRole: string;
}

const DEFAULT_HOME_EN_DEMO_INVOICE_PAGE_LIST_CONFIG = {
  bodyContent: { children: [["if", "@config.bodySearch", { children: [{ className: "w-full max-w-md", clearable: true, event: "REFETCH_QUERY", placeholder: "@config.searchPlaceholder", type: "search-input" }], direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], ["if", [">", ["array/len", "@config.filters"], 0], { children: [{ entity: "BrowseItem", event: "REFETCH_FILTER", filters: "@config.filters", look: "@config.filterBarLook", type: "filter-group" }], direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], { cols: "@config.cols", emptyDescription: "@config.emptyDescription", emptyIcon: "@config.emptyIcon", emptyTitle: "@config.emptyTitle", entity: "@payload.data", fields: "@config.fields", gap: "@config.gap", imageField: "@config.imageField", itemActions: ["array/filter", "@config.itemActions", ["fn", "item", ["or", ["=", ["array/len", ["object/get", "@item", "roles", []]], 0], ["array/includes", ["object/get", "@item", "roles", []], "@config.viewerRole"]]]], itemClickEvent: "@config.itemClickEvent", itemEnter: "@config.itemEnter", maxInlineActions: "@config.maxInlineActions", pageSize: "@config.displayPageSize", surface: "@config.surface", type: "data-grid" }], direction: "vertical", gap: "md", type: "stack" },
  bodySearch: false,
  browseLook: "feed",
  cols: 1,
  columns: [{ field: "name", header: ["i18n/t", "std-almadar-demo-invoices-page:invoice.number"], key: "name", weight: "medium" }, { field: "customer", header: ["i18n/t", "std-almadar-demo-invoices-page:invoice.customer"], key: "customer" }, { align: "right", field: "amount", format: "currency", header: ["i18n/t", "std-almadar-demo-invoices-page:line.amount"], key: "amount" }, { colorMap: { draft: "default", overdue: "error", paid: "success", sent: "info" }, field: "status", format: "badge", header: ["i18n/t", "std-almadar-demo-invoices-page:invoice.status"], key: "status", labels: { draft: ["i18n/t", "std-almadar-demo-invoices-page:status.draft"], overdue: ["i18n/t", "std-almadar-demo-invoices-page:status.overdue"], paid: ["i18n/t", "std-almadar-demo-invoices-page:status.paid"], sent: ["i18n/t", "std-almadar-demo-invoices-page:status.sent"] } }],
  denseBodyContent: { children: [["if", "@config.bodySearch", { children: [{ className: "w-full max-w-md", clearable: true, event: "REFETCH_QUERY", placeholder: "@config.searchPlaceholder", type: "search-input" }], className: "px-card-md py-card-sm border-b border-[var(--color-border)]", direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], ["if", [">", ["array/len", "@config.filters"], 0], { children: [{ entity: "BrowseItem", event: "REFETCH_FILTER", filters: "@config.filters", look: "@config.filterBarLook", type: "filter-group" }], className: "px-card-md py-card-sm border-b border-[var(--color-border)]", direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], { children: [{ columns: "@config.columns", emptyDescription: "@config.emptyDescription", emptyIcon: "@config.emptyIcon", emptyTitle: "@config.emptyTitle", entity: "@payload.data", itemActions: ["array/filter", "@config.itemActions", ["fn", "item", ["or", ["=", ["array/len", ["object/get", "@item", "roles", []]], 0], ["array/includes", ["object/get", "@item", "roles", []], "@config.viewerRole"]]]], itemClickEvent: "@config.itemClickEvent", look: "dense", maxInlineActions: "@config.maxInlineActions", pageSize: "@config.displayPageSize", selectable: "@config.selectable", surface: "none", type: "table-view" }], className: "overflow-x-auto", direction: "vertical", gap: "none", type: "stack" }], className: ["if", ["=", "@config.surface", "auto"], "surface-content overflow-hidden", ""], direction: "vertical", gap: "none", type: "stack" },
  detailActions: [{ event: "CLOSE", label: "Close" }],
  detailCloseEvent: "CLOSE",
  detailFields: [{ label: "Name", name: "name", variant: "h4" }, { label: "Description", name: "description", variant: "body" }, { label: "Status", name: "status", variant: "badge" }, { format: "date", label: "Created", name: "createdAt" }],
  displayPageSize: 10,
  emptyDescription: "",
  emptyIcon: "inbox",
  emptyTitle: "",
  feedBodyContent: { children: [["if", "@config.bodySearch", { align: "center", children: [{ className: "w-full", clearable: true, event: "REFETCH_QUERY", placeholder: "@config.searchPlaceholder", type: "search-input" }], className: "w-full", direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], ["if", [">", ["array/len", "@config.filters"], 0], { children: [{ entity: "BrowseItem", event: "REFETCH_FILTER", filters: "@config.filters", look: "@config.filterBarLook", type: "filter-group" }], className: "w-full", direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], { emptyDescription: "@config.emptyDescription", emptyIcon: "@config.emptyIcon", emptyTitle: "@config.emptyTitle", entity: "@payload.data", fields: "@config.fields", gap: "none", itemActions: ["array/filter", "@config.itemActions", ["fn", "item", ["or", ["=", ["array/len", ["object/get", "@item", "roles", []]], 0], ["array/includes", ["object/get", "@item", "roles", []], "@config.viewerRole"]]]], itemClickEvent: "@config.itemClickEvent", itemEnter: "@config.itemEnter", look: ["if", ["=", "@config.feedSurface", "cards"], "card-rows", "borderless"], maxInlineActions: "@config.maxInlineActions", pageSize: "@config.displayPageSize", surface: ["if", ["=", "@config.feedSurface", "plain"], "none", "@config.surface"], type: "data-list", variant: ["if", ["=", "@config.feedSurface", "divided"], "card", "default"] }], className: "w-full", direction: "vertical", gap: "lg", type: "stack" },
  feedSurface: "divided",
  fields: [{ name: "name", variant: "h4" }, { name: "customer", variant: "small" }, { format: "currency", name: "amount", variant: "small" }, { colorMap: { draft: "default", overdue: "error", paid: "success", sent: "info" }, labels: { draft: ["i18n/t", "std-almadar-demo-invoices-page:status.draft"], overdue: ["i18n/t", "std-almadar-demo-invoices-page:status.overdue"], paid: ["i18n/t", "std-almadar-demo-invoices-page:status.paid"], sent: ["i18n/t", "std-almadar-demo-invoices-page:status.sent"] }, name: "status", variant: "badge" }],
  filterBarLook: "toolbar",
  filters: [],
  galleryBodyContent: { children: [["if", "@config.bodySearch", { align: "center", children: [{ className: "w-full", clearable: true, event: "REFETCH_QUERY", placeholder: "@config.searchPlaceholder", type: "search-input" }], className: "sticky top-0 z-10 py-3 bg-[var(--color-surface)]/95 backdrop-blur", direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], ["if", [">", ["array/len", "@config.filters"], 0], { children: [{ entity: "BrowseItem", event: "REFETCH_FILTER", filters: "@config.filters", look: "@config.filterBarLook", type: "filter-group" }], className: "w-full", direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], { className: "w-full", emptyDescription: "@config.emptyDescription", emptyIcon: "@config.emptyIcon", emptyTitle: "@config.emptyTitle", entity: "@payload.data", fields: "@config.fields", gap: "lg", imageField: "@config.imageField", itemActions: ["array/filter", "@config.itemActions", ["fn", "item", ["or", ["=", ["array/len", ["object/get", "@item", "roles", []]], 0], ["array/includes", ["object/get", "@item", "roles", []], "@config.viewerRole"]]]], itemClickEvent: "@config.itemClickEvent", itemEnter: "@config.itemEnter", maxInlineActions: "@config.maxInlineActions", minCardWidth: 260, surface: "@config.surface", type: "data-grid" }], className: "w-full", direction: "vertical", gap: "lg", type: "stack" },
  gap: "md",
  imageField: "",
  include: [],
  initialFilterField: "lane",
  initialFilterValue: "ledger",
  itemActions: [],
  itemClickEvent: "VIEW",
  itemEnter: "rise",
  masterDetailBodyContent: { children: [["if", "@config.bodySearch", { children: [{ className: "w-full max-w-md", clearable: true, event: "REFETCH_QUERY", placeholder: "@config.searchPlaceholder", type: "search-input" }], className: "px-card-md py-card-sm border-b border-[var(--color-border)]", direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], ["if", [">", ["array/len", "@config.filters"], 0], { children: [{ entity: "BrowseItem", event: "REFETCH_FILTER", filters: "@config.filters", look: "@config.filterBarLook", type: "filter-group" }], className: "px-card-md py-card-sm border-b border-[var(--color-border)]", direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], { detail: { children: [], gap: "none", type: "stack" }, emptyDetail: { description: "Select a record from the list to see its details.", icon: "mouse-pointer-click", title: "Nothing selected", type: "empty-state" }, hasSelection: false, master: { children: ["@trait.HomeENHomeInvoicesDemoMasterListView"], className: "overflow-y-auto", direction: "vertical", gap: "none", type: "stack" }, type: "master-detail-layout" }], className: ["if", ["=", "@config.surface", "auto"], "surface-content overflow-hidden", ""], direction: "vertical", gap: "none", type: "stack" },
  masterDetailRecordBodyContent: { children: [["if", "@config.bodySearch", { children: [{ className: "w-full max-w-md", clearable: true, event: "REFETCH_QUERY", placeholder: "@config.searchPlaceholder", type: "search-input" }], className: "px-card-md py-card-sm border-b border-[var(--color-border)]", direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], ["if", [">", ["array/len", "@config.filters"], 0], { children: [{ entity: "BrowseItem", event: "REFETCH_FILTER", filters: "@config.filters", look: "@config.filterBarLook", type: "filter-group" }], className: "px-card-md py-card-sm border-b border-[var(--color-border)]", direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], { detail: { actions: ["array/filter", "@config.detailActions", ["fn", "action", ["or", ["=", ["array/len", ["object/get", "@action", "roles", []]], 0], ["array/includes", ["object/get", "@action", "roles", []], "@config.viewerRole"]]]], closeEvent: "@config.detailCloseEvent", fields: "@config.detailFields", initialData: "@payload.row", showActions: true, surface: "none", type: "detail-panel" }, hasSelection: true, master: { children: ["@trait.HomeENHomeInvoicesDemoMasterListView"], className: "overflow-y-auto", direction: "vertical", gap: "none", type: "stack" }, type: "master-detail-layout" }], className: ["if", ["=", "@config.surface", "auto"], "surface-content overflow-hidden", ""], direction: "vertical", gap: "none", type: "stack" },
  maxInlineActions: 3,
  orderBy: "position",
  pageSize: 10,
  scopeField: "",
  searchField: "name",
  searchPlaceholder: "Search…",
  selectable: false,
  surface: "auto",
  triageBodyContent: { children: [["if", "@config.bodySearch", { children: [{ className: "w-full max-w-md", clearable: true, event: "REFETCH_QUERY", placeholder: "@config.searchPlaceholder", type: "search-input" }], className: "px-card-md py-card-sm border-b border-[var(--color-border)]", direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], ["if", [">", ["array/len", "@config.filters"], 0], { children: [{ entity: "BrowseItem", event: "REFETCH_FILTER", filters: "@config.filters", look: "@config.filterBarLook", type: "filter-group" }], className: "px-card-md py-card-sm border-b border-[var(--color-border)]", direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], { children: [{ emptyDescription: "@config.emptyDescription", emptyIcon: "@config.emptyIcon", emptyTitle: "@config.emptyTitle", entity: "@payload.data", fields: "@config.fields", itemActions: ["array/filter", "@config.itemActions", ["fn", "item", ["or", ["=", ["array/len", ["object/get", "@item", "roles", []]], 0], ["array/includes", ["object/get", "@item", "roles", []], "@config.viewerRole"]]]], itemClickEvent: "@config.itemClickEvent", itemEnter: "@config.itemEnter", maxInlineActions: "@config.maxInlineActions", pageSize: "@config.displayPageSize", surface: "none", type: "data-list", variant: "compact" }], className: "overflow-x-auto", direction: "vertical", gap: "none", type: "stack" }], className: ["if", ["=", "@config.surface", "auto"], "surface-content overflow-hidden", ""], direction: "vertical", gap: "none", type: "stack" },
  viewerRole: "",
} as HomeENDemoInvoicePageListConfig;

export interface HomeENDemoInvoicePageListLogicReturn {
  state: HomeENDemoInvoicePageListState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeENDemoInvoicePageListEvent | null;
  slotWriter: Partial<Record<HomeENDemoInvoicePageListSlot, HomeENDemoInvoicePageListEvent | null>>;
  loading: boolean;
  pendingEvent: HomeENDemoInvoicePageListEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeENDemoInvoicePageItem>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof HomeENDemoInvoicePageListEventPayloadMap]: (payload?: HomeENDemoInvoicePageListEventPayloadMap[K]) => Promise<EventResponse> };
  config: HomeENDemoInvoicePageListConfig;
}

const __CASCADE_EVENTS = new Set<string>(['INVOICE_FIVE_CHANGED', 'INVOICE_ONE_CHANGED', 'REFETCH_FILTER']);

const __LEG_EVENTS: ReadonlySet<string> = new Set(['INIT', 'REFETCH_FILTER', 'REFETCH_PAGE', 'REFETCH_QUERY']);

export function useHomeENDemoInvoicePageListLogic(config?: Partial<HomeENDemoInvoicePageListConfig>): HomeENDemoInvoicePageListLogicReturn {
  const mergedConfig: HomeENDemoInvoicePageListConfig = { ...DEFAULT_HOME_EN_DEMO_INVOICE_PAGE_LIST_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/home-en-demo-invoice-page-list', orbitalName: 'HomeEN', traitName: 'HomeENDemoInvoicePageList' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(homeENDemoInvoicePageListReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const cascadeReceivedRef = useRef<TraitStateSnapshot['cascadeReceived']>([]);

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const { t: __t, locale: __locale } = useTranslate();
  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, _payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const entity: EntityRow[] = liveData['HomeENDemoInvoicePageItem'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};

    if (fromState === 'loading' && event === 'INIT') {
      // loading → loading (on INIT)
        if (false) {

        } else {

          if (true) {

          } else {

          }

        }

      _newState = 'loading';
    } else if (fromState === 'loading' && event === 'BrowseItemLoaded') {
      // loading → browsing (on BrowseItemLoaded)
        if (false) {

        } else {

          if (true) {

          } else {

            if (false) {

            } else {

              if (false) {

              } else {

                if (false) {

                } else {

                }

              }

            }

          }

        }

      _newState = 'browsing';
    } else if (fromState === 'loading' && event === 'BrowseItemLoadFailed') {
      // loading → error (on BrowseItemLoadFailed)
      _newState = 'error';
    } else if (fromState === 'browsing' && event === 'INIT') {
      // browsing → loading (on INIT)
      _newState = 'loading';
    } else if (fromState === 'browsing' && event === 'REFETCH_QUERY') {
      // browsing → browsing (on REFETCH_QUERY)
      _newState = 'browsing';
    } else if (fromState === 'browsing' && event === 'REFETCH_FILTER') {
      // browsing → browsing (on REFETCH_FILTER)
      _newState = 'browsing';
    } else if (fromState === 'browsing' && event === 'REFETCH_PAGE') {
      // browsing → browsing (on REFETCH_PAGE)
      _newState = 'browsing';
    } else if (fromState === 'browsing' && event === 'VIEW' && (false)) {
      // browsing → browsing (on VIEW)
      _newState = 'browsing';
    } else if (fromState === 'browsing' && event === 'CLOSE' && (false)) {
      // browsing → browsing (on CLOSE)
      _newState = 'browsing';
    } else if (fromState === 'browsing' && event === 'BrowseItemLoaded') {
      // browsing → browsing (on BrowseItemLoaded)
        if (false) {

        } else {

          if (true) {

          } else {

            if (false) {

            } else {

              if (false) {

              } else {

                if (false) {

                } else {

                }

              }

            }

          }

        }

      _newState = 'browsing';
    } else if (fromState === 'browsing' && event === 'BrowseItemLoadFailed') {
      // browsing → browsing (on BrowseItemLoadFailed)
      _newState = 'browsing';
    } else if (fromState === 'error' && event === 'INIT') {
      // error → loading (on INIT)
        if (false) {

        } else {

          if (true) {

          } else {

          }

        }

      _newState = 'loading';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: HomeENDemoInvoicePageListSlot[]; clear: HomeENDemoInvoicePageListSlot[] } => {
    switch (fromState) {
      case 'browsing':
        switch (event) {
          case 'BrowseItemLoadFailed': return { write: ['toast'] as HomeENDemoInvoicePageListSlot[], clear: [] as HomeENDemoInvoicePageListSlot[] };
          case 'BrowseItemLoaded': return { write: ['main'] as HomeENDemoInvoicePageListSlot[], clear: [] as HomeENDemoInvoicePageListSlot[] };
          case 'CLOSE': return { write: ['main'] as HomeENDemoInvoicePageListSlot[], clear: [] as HomeENDemoInvoicePageListSlot[] };
          case 'VIEW': return { write: ['main'] as HomeENDemoInvoicePageListSlot[], clear: [] as HomeENDemoInvoicePageListSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'error':
        switch (event) {
          case 'INIT': return { write: ['main'] as HomeENDemoInvoicePageListSlot[], clear: [] as HomeENDemoInvoicePageListSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'loading':
        switch (event) {
          case 'BrowseItemLoadFailed': return { write: ['main'] as HomeENDemoInvoicePageListSlot[], clear: [] as HomeENDemoInvoicePageListSlot[] };
          case 'BrowseItemLoaded': return { write: ['main'] as HomeENDemoInvoicePageListSlot[], clear: [] as HomeENDemoInvoicePageListSlot[] };
          case 'INIT': return { write: ['main'] as HomeENDemoInvoicePageListSlot[], clear: [] as HomeENDemoInvoicePageListSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'HomeENDemoInvoicePageItem': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as HomeENDemoInvoicePageListEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    if (__CASCADE_EVENTS.has(event)) {
      cascadeReceivedRef.current.push({ event, ...(payload !== undefined ? { payload } : {}), timestamp: Date.now() });
    }
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    if (__LEG_EVENTS.has(event)) {
    // Server leg: ordered by the pump, never blocking the drain.
    commandPump.enqueue(async () => {
      try {
        const response = await sendEvent(event, payload, prevState);
        if (response.success) {
          // Agreement — merge any fetch/persist results the local commit
          // couldn't supply (same reducer action, deferred leg).
          if (response.data && Object.keys(response.data).length > 0) {
            dispatchReducer({
              type: 'EVENT_SUCCESS',
              event: event as HomeENDemoInvoicePageListEvent,
              newState: machineStateRef.current,
              data: response.data,
              renderBearing: false,
            });
          }
        } else {
          // Rejection or state disagreement — roll back to the
          // pre-dispatch snapshot; the server stays authoritative.
          dispatchReducer({
            type: 'EVENT_SUCCESS',
            event: event as HomeENDemoInvoicePageListEvent,
            newState: prevState,
            data: {},
            fields: _snapshot,
            renderBearing: false,
          });
          machineStateRef.current = prevState;
          fieldsRef.current = { ...fieldsRef.current, ..._snapshot } as EntityRow;
          dispatchReducer({ type: 'EVENT_ERROR', error: response.error ?? `server state mismatch: expected ${_optimistic.newState}, got ${response.states?.['HomeENDemoInvoicePageList'] ?? '(unchanged)'}` });
        }
      } catch (err) {
        // Pump isolates the rejection; surface it without rolling
        // back — local state may simply be ahead of a reconnecting server.
        dispatchReducer({ type: 'EVENT_ERROR', error: err instanceof Error ? err.message : 'Network error' });
      }
    });
    }
    return { success: true, transitioned: true, states: { 'HomeENDemoInvoicePageList': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof HomeENDemoInvoicePageListEventPayloadMap]: (payload?: HomeENDemoInvoicePageListEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'BrowseItemLoaded': (payload?: HomeENDemoInvoicePageListEventPayloadMap['BrowseItemLoaded']) => dispatch('BrowseItemLoaded', payload),
    'BrowseItemLoadFailed': (payload?: HomeENDemoInvoicePageListEventPayloadMap['BrowseItemLoadFailed']) => dispatch('BrowseItemLoadFailed', payload),
    'REFETCH_QUERY': (payload?: HomeENDemoInvoicePageListEventPayloadMap['REFETCH_QUERY']) => dispatch('REFETCH_QUERY', payload),
    'REFETCH_FILTER': (payload?: HomeENDemoInvoicePageListEventPayloadMap['REFETCH_FILTER']) => dispatch('REFETCH_FILTER', payload),
    'REFETCH_PAGE': (payload?: HomeENDemoInvoicePageListEventPayloadMap['REFETCH_PAGE']) => dispatch('REFETCH_PAGE', payload),
    'VIEW': (payload?: HomeENDemoInvoicePageListEventPayloadMap['VIEW']) => dispatch('VIEW', payload),
    'CLOSE': () => dispatch('CLOSE'),
    'EDIT': (payload?: HomeENDemoInvoicePageListEventPayloadMap['EDIT']) => dispatch('EDIT', payload),
    'DELETE': (payload?: HomeENDemoInvoicePageListEventPayloadMap['DELETE']) => dispatch('DELETE', payload),
  };

  useUIEvents(enqueueEvent, 'HomeEN.HomeENDemoInvoicePageList', ['INIT', 'BrowseItemLoaded', 'BrowseItemLoadFailed', 'REFETCH_QUERY', 'REFETCH_FILTER', 'REFETCH_PAGE', 'VIEW', 'CLOSE'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('HomeENDemoInvoicePageList', (): TraitStateSnapshot => ({
      traitName: 'HomeENDemoInvoicePageList',
      currentState: internalStateRef.current.machineState,
      states: ['loading', 'browsing', 'error'],
      events: ['INIT', 'BrowseItemLoaded', 'BrowseItemLoadFailed', 'REFETCH_QUERY', 'REFETCH_FILTER', 'REFETCH_PAGE', 'VIEW', 'CLOSE', 'EDIT', 'DELETE'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: cascadeReceivedRef.current,
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'INVOICE_ONE_CHANGED' (from HomeENDemoInvoicePageDesk) → trigger 'REFETCH_FILTER'
    const unsub_invoice_one_changed_from_homeendemoinvoicepagedesk_to_refetch_filter = eventBus.on('UI:HomeEN.HomeENDemoInvoicePageDesk.INVOICE_ONE_CHANGED', (event: BusEvent) => {
      if (!event.source || !(event.source as { fromBridge?: boolean }).fromBridge) {
        return;
      }
      if (!claimDelivery(event.source, 'HomeEN.HomeENDemoInvoicePageList', 'REFETCH_FILTER')) return;
      enqueueEvent('REFETCH_FILTER', { entity: "DemoInvoicePageItem", field: "lane", value: "ledger" } as EventPayload);
    });
    // Listen for 'INVOICE_FIVE_CHANGED' (from HomeENDemoInvoicePageDesk) → trigger 'REFETCH_FILTER'
    const unsub_invoice_five_changed_from_homeendemoinvoicepagedesk_to_refetch_filter = eventBus.on('UI:HomeEN.HomeENDemoInvoicePageDesk.INVOICE_FIVE_CHANGED', (event: BusEvent) => {
      if (!event.source || !(event.source as { fromBridge?: boolean }).fromBridge) {
        return;
      }
      if (!claimDelivery(event.source, 'HomeEN.HomeENDemoInvoicePageList', 'REFETCH_FILTER')) return;
      enqueueEvent('REFETCH_FILTER', { entity: "DemoInvoicePageItem", field: "lane", value: "ledger" } as EventPayload);
    });
    return () => {
      if (typeof unsub_invoice_one_changed_from_homeendemoinvoicepagedesk_to_refetch_filter === 'function') unsub_invoice_one_changed_from_homeendemoinvoicepagedesk_to_refetch_filter();
      if (typeof unsub_invoice_five_changed_from_homeendemoinvoicepagedesk_to_refetch_filter === 'function') unsub_invoice_five_changed_from_homeendemoinvoicepagedesk_to_refetch_filter();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
